import { zodResolver } from "@hookform/resolvers/zod";
import {
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { AxiosError } from "axios";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useRequestAccountDeletion } from "./api/useRequestAccountDeletion";

const MINIMUM_CHARACTER_COUNT_FOR_REASON_FIELD = 10;

const deleteAccountFormSchema = z.object({
  reason: z.string().min(MINIMUM_CHARACTER_COUNT_FOR_REASON_FIELD, {
    message: `Please provide at least ${MINIMUM_CHARACTER_COUNT_FOR_REASON_FIELD} characters.`,
  }),
  confirmation: z.boolean().refine((value) => value, { message: "You must confirm to proceed." }),
});

interface DeleteAccountDialogProps {
  modalState: UseModalState;
  onSuccess: () => void;
  onFailure: () => void;
}

export function DeleteAccountDialog(props: DeleteAccountDialogProps) {
  const { modalState, onSuccess, onFailure } = props;

  const formMethods = useForm({
    defaultValues: {
      reason: "",
      confirmation: false,
    },
    resolver: zodResolver(deleteAccountFormSchema),
  });

  const {
    formState: { errors, isSubmitting, isValid: formIsValid },
    register,
    handleSubmit,
  } = formMethods;

  const { mutateAsync: requestAccountDeletion, isLoading: isDeletingAccount } =
    useRequestAccountDeletion();

  const { showErrorToast } = useToast();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <FullScreenDialog modalState={modalState} variant="tertiary">
      <StickyScrollPageHeader
        headerVariant="tertiary"
        title="Delete Account"
        scrollContainerRef={scrollContainerRef}
        onClickBack={() => {
          logEvent(APP_V2_USER_EVENTS.REQUEST_ACCOUNT_DELETION_CANCEL_AT_CONFIRM);
          modalState.closeModal();
        }}
      />
      <DialogContent>
        <Stack
          ref={scrollContainerRef}
          sx={{ flexDirection: "column", flex: 1, gap: 4, overflow: "auto" }}
        >
          <Text>
            Please note, when your account is deleted you will no longer be able to access it, or
            any data associated with the account.
          </Text>
          <Text>Additionally, any booked shifts you have will be cancelled.</Text>
          <Text sx={{ fontWeight: "bold" }}>
            Standard cancellation penalties will apply to your clipboard score for any cancelled
            shifts.
          </Text>
          <Text>
            If you choose to proceed, we will process your request and notify you with the outcome
            within 45 days.
          </Text>

          <TextField
            fullWidth
            multiline
            required
            label="Reason for deletion"
            placeholder={`Minimum ${MINIMUM_CHARACTER_COUNT_FOR_REASON_FIELD} characters`}
            sx={{ mt: 4 }}
            rows={4}
            {...register("reason")}
            error={Boolean(errors.reason)}
            helperText={errors.reason?.message}
          />
          <FormControlLabel
            control={<Checkbox {...register("confirmation")} sx={{ alignSelf: "flex-start" }} />}
            label="I understand that deleting my account is not reversible and I will no longer be able to log in to Clipboard."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexDirection: "column" }}>
        <LoadingButton
          fullWidth
          variant="destructive"
          startIconType="remove"
          isLoading={isSubmitting || isDeletingAccount}
          disabled={!formIsValid}
          onClick={handleSubmit(async (data) => {
            logEvent(APP_V2_USER_EVENTS.REQUEST_ACCOUNT_DELETION_CONFIRM);

            try {
              await requestAccountDeletion(data);
              onSuccess();
            } catch (error) {
              if (error instanceof AxiosError) {
                if (error.response?.status === 409) {
                  showErrorToast(
                    "You have already requested account deletion. Please wait for it to be processed."
                  );
                }
              } else {
                showErrorToast(
                  "Something went wrong while requesting account deletion. Please try again."
                );
              }

              onFailure();
            }
          })}
        >
          Confirm Account Deletion
        </LoadingButton>
      </DialogActions>
    </FullScreenDialog>
  );
}
